(() => {
  const STORAGE_KEY = 'compareProducts';
  const MAX_PRODUCTS = 4;
  const COMPARE_URL = '/pages/compare-products';
  const BAR_SELECTOR = '[data-gvs-compare-bar]';
  const BUTTON_SELECTOR = '.compare-btn[data-compare-id], .compare-btn[data-product-id]';
  const PAGE_SELECTOR = '[data-gvs-compare-products]';
  const STORAGE_VERSION = 2;

  let barResizeObserver;
  let footerObserver;
  let refreshFrame = 0;

  const toString = (value) => value === null || value === undefined ? '' : String(value);

  const normaliseItem = (item = {}) => {
    const handle = toString(item.handle || item.productHandle || item.product_handle).trim();
    const id = toString(item.id || item.productId || item.product_id || handle).trim();

    if (!id || !handle) return null;

    return {
      version: STORAGE_VERSION,
      id,
      handle,
      url: toString(item.url || `/products/${handle}`).trim(),
      title: toString(item.title || item.name || 'Product').trim(),
      image: toString(
        typeof item.image === 'string'
          ? item.image
          : item.image?.src || item.featured_image?.src || item.featuredImage?.src || ''
      ).trim(),
      variantId: toString(item.variantId || item.variant_id || '').trim(),
      variantExplicit: Boolean(item.variantExplicit || item.variant_explicit),
      addedAt: Number(item.addedAt || Date.now()),
    };
  };

  const sameStoredItems = (left, right) => JSON.stringify(left) === JSON.stringify(right);

  function readProducts({ migrate = true } = {}) {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];

      const seen = new Set();
      const products = parsed
        .map(normaliseItem)
        .filter(Boolean)
        .filter((item) => {
          const key = item.id || item.handle;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, MAX_PRODUCTS);

      if (migrate && !sameStoredItems(parsed, products)) {
        writeProducts(products, { dispatch: false });
      }

      return products;
    } catch (_) {
      return [];
    }
  }

  function writeProducts(products, { dispatch = true } = {}) {
    const normalised = products.map(normaliseItem).filter(Boolean).slice(0, MAX_PRODUCTS);

    try {
      if (normalised.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalised));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {
      announce('Your comparison could not be saved in this browser.');
      return false;
    }

    if (dispatch) {
      window.dispatchEvent(new CustomEvent('gvs:compare-updated', {
        detail: { products: normalised },
      }));
    }

    return true;
  }

  function getLiveRegion() {
    let region = document.getElementById('gvs-compare-live-region');

    if (!region) {
      region = document.createElement('div');
      region.id = 'gvs-compare-live-region';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }

    return region;
  }

  function announce(message) {
    const region = getLiveRegion();
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 30);
  }

  function currentVariantId(button) {
    const form = button.closest('product-form-component, form, .product-form-buttons');
    const variantInput = form?.querySelector('input[name="id"], [ref="variantId"]');
    return toString(variantInput?.value || button.dataset.compareVariantId).trim();
  }

  function itemFromButton(button) {
    const legacyData = button.closest('.product-form-buttons')?.querySelector('.compare-product-data');
    const dataset = button.dataset;
    const legacy = legacyData?.dataset || {};
    const handle = dataset.compareHandle || dataset.productHandle || legacy.handle;
    const id = dataset.compareId || dataset.productId || legacy.id;

    return normaliseItem({
      id,
      handle,
      url: dataset.compareUrl || legacy.url,
      title: dataset.compareTitle || legacy.title,
      image: dataset.compareImage || legacy.image,
      variantId: currentVariantId(button) || legacy.variantId,
      variantExplicit: dataset.compareExplicitVariant === 'true',
      addedAt: Date.now(),
    });
  }

  function setButtonLabel(button, state, item) {
    const cardLink = button.dataset.compareCardLink === 'true';
    const compact = !cardLink && (button.dataset.compareCompact === 'true' || button.classList.contains('c-product-card__compare'));
    const label = button.querySelector('[data-compare-label]') || button;
    const title = item?.title || button.dataset.compareTitle || 'product';

    let text = cardLink ? 'Add to comparison' : compact ? 'Compare' : 'Add to comparison';
    let ariaLabel = `Add ${title} to comparison`;

    if (state === 'selected') {
      text = cardLink ? 'Added to comparison' : compact ? 'Compared' : 'Remove from comparison';
      ariaLabel = `Remove ${title} from comparison`;
    } else if (state === 'update') {
      text = cardLink ? 'Update comparison' : compact ? 'Update' : 'Update comparison';
      ariaLabel = `Update ${title} in comparison`;
    }

    label.textContent = text;
    button.setAttribute('aria-label', ariaLabel);
  }

  function updateButtons(products = readProducts()) {
    const byId = new Map(products.map((item) => [toString(item.id), item]));
    const byHandle = new Map(products.map((item) => [item.handle, item]));

    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const id = toString(button.dataset.compareId || button.dataset.productId);
      const handle = toString(button.dataset.compareHandle || button.dataset.productHandle);
      const selected = byId.get(id) || byHandle.get(handle);
      const variantId = currentVariantId(button);
      const shouldUpdate = Boolean(
        selected &&
        button.dataset.compareExplicitVariant === 'true' &&
        variantId &&
        selected.variantId &&
        variantId !== selected.variantId
      );

      const state = shouldUpdate ? 'update' : selected ? 'selected' : 'idle';
      button.setAttribute('aria-pressed', String(Boolean(selected)));
      button.classList.toggle('is-selected', state === 'selected');
      button.classList.toggle('is-update', state === 'update');
      setButtonLabel(button, state, selected);
    });

    document.querySelectorAll('[data-compare-inline-link]').forEach((link) => {
      link.hidden = products.length === 0;
      const count = link.querySelector('[data-compare-inline-count]');
      if (count) count.textContent = String(products.length);
    });
  }

  function thumbnailMarkup(item) {
    if (!item.image) {
      return `<span class="compare-floating-thumb compare-floating-thumb--empty" aria-hidden="true">+</span>`;
    }

    return `<img class="compare-floating-thumb" src="${escapeAttribute(item.image)}" alt="" loading="lazy" width="38" height="38">`;
  }

  function escapeAttribute(value) {
    return toString(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function createCompareBar() {
    const bar = document.createElement('aside');
    bar.className = 'compare-floating-bar';
    bar.hidden = true;
    bar.dataset.gvsCompareBar = '';
    bar.setAttribute('aria-label', 'Saved product comparison');

    bar.innerHTML = `
      <div class="compare-floating-content">
        <div class="compare-floating-thumbs" data-compare-bar-thumbs aria-hidden="true"></div>
        <div class="compare-floating-copy">
          <strong class="compare-count" data-compare-bar-count></strong>
          <span class="compare-floating-note">Compare live prices and specifications</span>
        </div>
        <div class="compare-floating-actions">
          <button type="button" class="compare-clear-btn" data-compare-bar-clear>Clear</button>
          <a class="compare-view-btn" href="${COMPARE_URL}">Compare now</a>
        </div>
      </div>
    `;

    document.body.appendChild(bar);

    bar.querySelector('[data-compare-bar-clear]')?.addEventListener('click', () => {
      const products = readProducts();
      writeProducts([]);
      announce(`${products.length} ${products.length === 1 ? 'product' : 'products'} removed from your comparison.`);
      updateInterface();
    });

    if ('ResizeObserver' in window) {
      barResizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--gvs-compare-bar-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`);
      });
      barResizeObserver.observe(bar);
    }

    setupFooterObserver(bar);
    return bar;
  }

  function setupFooterObserver(bar) {
    footerObserver?.disconnect();
    const footer = document.querySelector('[data-gvs-premium-footer], footer');
    if (!footer || !('IntersectionObserver' in window)) return;

    footerObserver = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.08);
      bar.classList.toggle('gvs-compare-footer-hidden', visible);
    }, { threshold: [0, 0.08, 0.2] });

    footerObserver.observe(footer);
  }

  function updateCompareBar(products) {
    let bar = document.querySelector(BAR_SELECTOR);

    // Do not create a hidden comparison bar (and its Resize/Intersection observers)
    // for the majority of visits that have no saved comparison state.
    if (!products.length && !bar) {
      document.body.classList.remove('gvs-compare-bar-visible');
      document.documentElement.style.removeProperty('--gvs-compare-bar-height');
      return;
    }

    bar ||= createCompareBar();
    const onComparePage = Boolean(document.querySelector(PAGE_SELECTOR));
    const shouldShow = products.length > 0 && !onComparePage;
    const count = bar.querySelector('[data-compare-bar-count]');
    const thumbs = bar.querySelector('[data-compare-bar-thumbs]');
    const noun = products.length === 1 ? 'product' : 'products';

    if (count) count.textContent = `${products.length} ${noun} selected`;
    if (thumbs) thumbs.innerHTML = products.slice(0, 4).map(thumbnailMarkup).join('');

    bar.hidden = !shouldShow;
    bar.classList.toggle('is-visible', shouldShow);
    document.body.classList.toggle('gvs-compare-bar-visible', shouldShow);

    if (shouldShow) {
      requestAnimationFrame(() => {
        if (!bar.hidden) {
          document.documentElement.style.setProperty('--gvs-compare-bar-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`);
        }
      });
    } else {
      document.documentElement.style.removeProperty('--gvs-compare-bar-height');
    }
  }

  function updateInterface() {
    refreshFrame = 0;
    const products = readProducts();
    const hasStaleSelectedButton = Boolean(
      document.querySelector('.compare-btn.is-selected, .compare-btn.is-update, .compare-btn[aria-pressed="true"]')
    );

    // Server-rendered compare buttons already represent the empty/idle state, so
    // avoid a page-wide querySelectorAll scan until there is comparison state to
    // reflect (or a bfcache-restored selected button that needs resetting).
    if (products.length || hasStaleSelectedButton) updateButtons(products);

    updateCompareBar(products);
    window.GVSPremiumFooterPolish?.refresh?.();
  }


  function scheduleInterfaceUpdate() {
    if (refreshFrame) return;
    refreshFrame = requestAnimationFrame(updateInterface);
  }

  function toggleProduct(button) {
    const item = itemFromButton(button);

    if (!item) {
      announce('Sorry, this product could not be added to the comparison.');
      return;
    }

    const products = readProducts();
    const existingIndex = products.findIndex((product) => product.id === item.id || product.handle === item.handle);

    if (existingIndex >= 0) {
      const existing = products[existingIndex];
      const isVariantUpdate = Boolean(
        item.variantExplicit &&
        item.variantId &&
        existing.variantId &&
        item.variantId !== existing.variantId
      );

      if (isVariantUpdate) {
        products[existingIndex] = { ...existing, ...item, addedAt: existing.addedAt };
        if (writeProducts(products)) {
          announce(`${item.title} updated in your comparison.`);
        }
      } else {
        products.splice(existingIndex, 1);
        if (writeProducts(products)) {
          announce(`${item.title} removed from your comparison.`);
        }
      }

      updateInterface();
      return;
    }

    if (products.length >= MAX_PRODUCTS) {
      announce(`You can compare up to ${MAX_PRODUCTS} products. Remove one before adding another.`);
      const bar = document.querySelector(BAR_SELECTOR);
      bar?.classList.remove('is-limit');
      requestAnimationFrame(() => bar?.classList.add('is-limit'));
      return;
    }

    products.push(item);
    if (writeProducts(products)) {
      announce(`${item.title} added to your comparison.`);
      updateInterface();
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    toggleProduct(button);
  });

  document.addEventListener('variant:update', (event) => {
    const productId = toString(event.detail?.data?.productId || event.target?.dataset?.productId);
    const variantId = toString(event.detail?.resource?.id);
    const scope = event.target instanceof Element ? event.target.closest('product-form-component, .product-information') : null;

    (scope || document).querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const buttonProductId = toString(button.dataset.compareId || button.dataset.productId);
      if (productId && buttonProductId && productId !== buttonProductId) return;
      if (variantId) button.dataset.compareVariantId = variantId;
      const media = event.detail?.resource?.featured_media?.preview_image?.src;
      if (media) button.dataset.compareImage = media;
    });

    updateButtons();
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) updateInterface();
  });
  window.addEventListener('gvs:compare-updated', updateInterface);
  window.addEventListener('pageshow', updateInterface);
  document.addEventListener('shopify:section:load', scheduleInterfaceUpdate);
  document.addEventListener('shopify:section:unload', scheduleInterfaceUpdate);
  document.addEventListener('filter:update', scheduleInterfaceUpdate);
  document.addEventListener('dialog:open', scheduleInterfaceUpdate);

  // Dynamic GVS/Horizon surfaces already emit explicit section, filter, variant
  // and dialog events. Avoid observing the entire <body> subtree permanently.

  window.GVSCompare = {
    read: readProducts,
    write: writeProducts,
    refresh: updateInterface,
    storageKey: STORAGE_KEY,
    maximum: MAX_PRODUCTS,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateInterface, { once: true });
  } else {
    updateInterface();
  }
})();
